import { SiteInfo } from '@sitecore-content-sdk/nextjs';
import baseSites from '.sitecore/sites.json';
import domains from 'scripts/domains/domains.json';
import { SiteDomainLocale } from 'lib/site-domain';

/**
 * Builds the full host -> site list used for host-based site resolution.
 *
 * The CSDK-generated `.sitecore/sites.json` only contains the hostnames defined
 * on each Site Grouping's `HostName` field. Custom domain mappings (the child
 * `SiteDomain` items, e.g. country domains like `dev.haagen-dazs.fr`) are NOT
 * included there, so the multisite middleware cannot resolve the correct site
 * for those hosts and falls back to the wildcard / default site.
 *
 * The domain query output (`scripts/domains/domains.json`, produced by
 * scripts/generate-domains.ts) DOES contain every domain together with its
 * `siteName`. We merge those entries in here so that every configured domain
 * resolves to its correct site by host.
 *
 * Notes:
 * - Base `sites.json` entries are added first so their explicit host mappings
 *   win on any duplicate-host tie (the SDK SiteResolver keeps the first
 *   encountered mapping for a given host).
 * - Wildcard hosts (e.g. `*`) are still supported; the SDK SiteResolver always
 *   sorts wildcards last by specificity, so exact custom domains win over them.
 * - When the domain query is disabled, `domains.json` is `[]` and the result is
 *   identical to the base `sites.json`.
 */
export function buildSites(): SiteInfo[] {
  const base = baseSites as SiteInfo[];
  const domainList = (domains as SiteDomainLocale[]) || [];

  const seenHosts = new Set<string>();
  const sites: SiteInfo[] = [];

  const addEntry = (name: string, hostName: string, language: string) => {
    hostName
      .split(/\||,|;/)
      .map((host) => host.trim().toLowerCase())
      .filter(Boolean)
      .forEach((host) => {
        if (seenHosts.has(host)) return;
        seenHosts.add(host);
        sites.push({ name, hostName: host, language });
      });
  };

  // 1. Base sites first (explicit Site Grouping host mappings win on ties).
  base.forEach((site) => addEntry(site.name, site.hostName, site.language));

  // 2. Custom domains from the domain query (covers child SiteDomain items).
  domainList.forEach((domain) => {
    if (!domain.siteName || !domain.domain) return;
    addEntry(domain.siteName, domain.domain, domain.defaultLocale);
    if (domain.previewDomain && domain.previewDomain !== domain.domain) {
      addEntry(domain.siteName, domain.previewDomain, domain.defaultLocale);
    }
    if (domain.siteGroupingHostName) {
      addEntry(domain.siteName, domain.siteGroupingHostName, domain.defaultLocale);
    }
  });

  return sites;
}

export const mergedSites = buildSites();
